import { parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Get a human-readable phone number from an input string.
 *
 * The formatter:
 * - accepts E164 and arbitrarily formatted phone numbers
 * - checks if the number is viable (i.e. has right number of digits for country)
 * - does not check that the number is valid (e.g. invalid area codes)
 * - assumes the number is from US/CAN if no country code is provided
 * - formats the number with the country code if the country is not US/CAN
 * - formats the number in national format if it is a US/CAN number
 */
export function formatPhoneNumber(input: string): string {
  const phoneNumber = parsePhoneNumberFromString(input, {
    extract: false,
    defaultCountry: 'US',
  });

  if (!phoneNumber) {
    return input; // Return the original input if parsing fails
  }

  // check that it's a viable phone number (i.e. has the right number of digits)
  if (!phoneNumber.isPossible()) {
    return input;
  }

  // number is american or canadian, format as a local number
  if (phoneNumber.countryCallingCode === '1') {
    return phoneNumber.formatNational();
  } else {
    return phoneNumber.formatInternational();
  }
}

export function toE164(mdn: string) {
  const phoneNumber = parsePhoneNumberFromString(mdn, {
    extract: false,
    defaultCountry: 'US',
  });

  if (!phoneNumber) {
    // legacy, method expected to return empty string if not parsable
    return '';
  }

  return phoneNumber.number;
}

// SEMI-HACK: we want to feature flag allowing input of mexican numbers,
// but we don't have any access to feature flags / state in the utils package,
// so expose a global "config" for the app to set based on the flags it receives
const validationConfig = {
  allowMexPhoneNumbers: false,
};

/**
 * Globally configure `isValidPhoneNumber` to accept Mexican phone numbers.
 * When set to false, only `+1` numbers will be accepted
 */
export const setAllowMexPhoneNumbers = (allowed: boolean) => {
  validationConfig.allowMexPhoneNumbers = allowed;
};

/**
 * Validate whether a phone number is possible and belongs to an allowed country code.
 *
 * By default, only +1 numbers are allowed, use `setAllowMexPhoneNumbers(true)`
 * to allow `+52` numbers as well.
 */
export const isValidNumber = (number: string): boolean => {
  let allowedCountryCodes;

  if (validationConfig.allowMexPhoneNumbers) {
    allowedCountryCodes = ['1', '52']; // Allow +1 and Mexico
  } else {
    allowedCountryCodes = ['1']; // Only allow +1
  }

  return isAllowedPhoneNumber(number, allowedCountryCodes);
};

/**
 * Returns true if the input string
 * - is a viable (possible) phone number
 * - has its country code match one of the allowed codes
 *
 * note:
 * - country codes are expected not to include the leading '+'
 */
export const isAllowedPhoneNumber = (
  input: string,
  allowedCountryCodes = ['1', '52'],
): boolean => {
  const phoneNumber = parsePhoneNumberFromString(input, {
    defaultCountry: 'US',
    extract: false,
  });

  // number not parsable
  if (!phoneNumber) {
    return false;
  }

  // check length of number based on country
  if (!phoneNumber.isPossible()) {
    return false;
  }

  const countryCode = phoneNumber.countryCallingCode;
  return allowedCountryCodes.includes(countryCode);
};

/**
 * Returns true if the input string could be a partial or complete phone number.
 *
 * Will return false for strings that contains letters, or that are too short
 * to determine if they could be a phone number (e.g. missing national significant digits).
 */
export const isPhoneNumberLike = (address: string): boolean => {
  return (
    parsePhoneNumberFromString(address, {
      defaultCountry: 'US',
      extract: false,
    }) !== undefined
  );
};

export const colorForAddress = (address: string | null | undefined) => {
  // Copied from mobile, should be consolidated
  if (!address || address.length <= 1) return '#DDD';
  const lastChar = address[address.length - 1];
  switch (lastChar) {
    case '0':
      return '#7C9885';
    case '1':
      return '#7A5980';
    case '2':
      return '#5C5D8D';
    case '3':
      return '#28666E';
    case '4':
      return '#698996';
    case '5':
      return '#E8D79D';
    case '6':
      return '#B2CEDE';
    case '7':
      return '#A8C69F';
    case '8':
      return '#99A1A6';
    case '9':
      return '#749E65';
    default:
      return '#DDDDDD';
  }
};

export const formattedAddress = (address: string, source: string): string => {
  switch (source) {
    case 'facebook':
      return 'via Facebook';
    case 'google':
      return 'via Google My Business';
    case 'sms':
    default:
      return formatPhoneNumber(address);
  }
};

const isBandwidthNumber = (lineType: string, carrierName: string) => {
  return lineType === 'VOIP' && carrierName.toLowerCase().includes('bandwidth');
};

const isSybaseNumber = (lineType: string, carrierName: string) => {
  return (
    lineType === 'VOIP' && carrierName.toLowerCase().includes('sms - sybase365')
  );
};

export const canTextEnablePhoneline = (
  lineType: string | null | undefined,
  carrierName: string | null | undefined,
) => {
  if (!lineType || !carrierName) {
    return false;
  }

  if (lineType === 'NUMA') {
    return true;
  }

  return (
    ['LANDLINE', 'VOIP'].includes(lineType) &&
    !isBandwidthNumber(lineType, carrierName) &&
    !isSybaseNumber(lineType, carrierName)
  );
};
